import React from 'react';
import { useTranslation } from 'react-i18next';

const fileTypePictograms = {
  'pdf': <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />,
  'doc': <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />,
  'docx': <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />,
  'xls': <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />,
  'xlsx': <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />,
  'ppt': <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />,
  'pptx': <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />,
  'default': <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 3h8l4 4v14H7z" />,
};

const getFileTypePictogram = (fileType) => {
  if (!fileType || typeof fileType !== 'string') return fileTypePictograms.default;
  return fileTypePictograms[fileType.toLowerCase()] || fileTypePictograms.default;
};

const getFileTypeExtension = (filename) => {
  return filename.split('.').pop();
};

const AttachmentsGallery = ({ attachments = [], loading = false, onImageClick, onDelete, editable = false }) => {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!attachments || attachments.length === 0) {
    return <p className="text-gray-500 text-center py-8">{t('No attachment')}</p>;
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
      {attachments.map((attachment, index) => {
        const isImage = attachment.filename && /(jpg|jpeg|png|gif|bmp|webp)$/i.test(attachment.filename);
        const fileUrl = attachment.url || attachment.content;

        if (isImage && fileUrl) {
          const extension = getFileTypeExtension(attachment.filename || '');
          return (
            <div key={index} className="relative group">
              <button
                type="button"
                className="w-full border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow flex flex-col"
                onClick={() => onImageClick && onImageClick({ url: fileUrl, filename: attachment.filename })}
              >
                <div className="aspect-video w-full overflow-hidden bg-gray-50">
                  <img
                    src={fileUrl}
                    alt={attachment.filename}
                    className="object-cover w-full h-full transition-transform duration-200 group-hover:scale-105"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                <div className="px-3 py-2">
                  <p className="text-[11px] font-medium text-gray-900 truncate text-left">
                    {attachment.filename}
                  </p>
                  <p className="mt-0.5 text-[10px] uppercase tracking-wide text-gray-400 text-left">
                    {extension || 'FILE'}
                  </p>
                </div>
              </button>
              {editable && onDelete && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(attachment, index);
                  }}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  title={t('Delete')}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          );
        }

        const extension = getFileTypeExtension(attachment.filename || '');
        const fileTypePictogram = getFileTypePictogram(extension);
        const thumbnailUrl = attachment.thumbnail;

        return (
          <div
            key={index}
            className="group border border-gray-200 rounded-xl bg-white overflow-hidden flex flex-col shadow-sm hover:shadow-md transition-shadow text-center relative"
          >
            <div className="aspect-video w-full flex items-center justify-center bg-gray-50 overflow-hidden">
              {thumbnailUrl ? (
                <img
                  src={thumbnailUrl}
                  alt={attachment.filename}
                  className="object-cover w-full h-full transition-transform duration-200 group-hover:scale-105"
                  onError={(e) => {
                    // Fallback to pictogram if thumbnail fails to load
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
              ) : null}
              <svg
                className="w-10 h-10 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{ display: thumbnailUrl ? 'none' : 'block' }}
              >
                {fileTypePictogram}
              </svg>
            </div>
            <div className="px-3 py-2 flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-medium text-gray-900 truncate text-left">
                  {attachment.filename}
                </p>
                {extension && (
                  <p className="mt-0.5 text-[10px] uppercase tracking-wide text-gray-400 text-left">
                    {extension || 'FILE'}
                  </p>
                )}
              </div>
              {fileUrl && (
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-1 inline-flex items-center justify-center rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 px-2 py-1"
                  aria-label={t('Download the file')}
                >
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M8 12l4 4m0 0l4-4m-4 4V4"
                    />
                  </svg>
                </a>
              )}
            </div>
            {editable && onDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(attachment, index);
                }}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                title={t('Delete')}
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default AttachmentsGallery;


